import { useState } from 'react';
import { Modal } from '../common/Modal';
import { LINK_TYPES } from '../common/ProfileLinks';
import { saveProfile, uploadBusinessInsuranceDoc } from '../../services/profileService';
import './ProfilePage.css';

const SERVICE_OPTIONS = [
  { id: 'tours',   label: 'Guided Tours' },
  { id: 'lessons', label: 'Coaching & Lessons' },
];

// The single business-application form, reused from two entry points:
// ProfilePage's "I'm a business" section, and the "Sign up as a business"
// flow in AuthBadge/App.jsx. Submitting always writes businessVerifiedStatus
// as 'pending' — 'verified' is admin-only, enforced in firestore.rules.
export function BusinessApplicationModal({ uid, initialData = {}, onClose, onSubmitted }) {
  const [businessName,     setBusinessName]     = useState(initialData.businessName || '');
  const [businessEmail,    setBusinessEmail]    = useState(initialData.businessEmail || '');
  const [businessPhone,    setBusinessPhone]    = useState(initialData.businessPhone || '');
  const [businessWebsite,  setBusinessWebsite]  = useState(initialData.businessWebsite || '');
  const [businessLinks,    setBusinessLinks]    = useState(Array.isArray(initialData.businessLinks) ? initialData.businessLinks : []);
  const [businessServices, setBusinessServices] = useState(Array.isArray(initialData.businessServices) ? initialData.businessServices : []);
  const [businessABN,      setBusinessABN]      = useState(initialData.businessABN || '');
  const [insuranceUrl,      setInsuranceUrl]      = useState(initialData.businessInsuranceUrl || '');
  const [insuranceFileName, setInsuranceFileName] = useState(initialData.businessInsuranceFileName || '');
  const [insuranceFile,     setInsuranceFile]     = useState(null); // pending upload
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  function updateLink(i, patch) {
    setBusinessLinks(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));
  }
  function addLink() {
    setBusinessLinks(ls => [...ls, { type: 'instagram', url: '' }]);
  }
  function removeLink(i) {
    setBusinessLinks(ls => ls.filter((_, j) => j !== i));
  }
  function toggleService(id) {
    setBusinessServices(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError('');
    try {
      let finalInsuranceUrl = insuranceUrl;
      let finalInsuranceFileName = insuranceFileName;
      if (insuranceFile) {
        finalInsuranceUrl = await uploadBusinessInsuranceDoc(uid, insuranceFile);
        finalInsuranceFileName = insuranceFile.name;
      }
      const fields = {
        isBusiness: true,
        businessName: businessName.trim(),
        businessEmail: businessEmail.trim(),
        businessPhone: businessPhone.trim(),
        businessWebsite: businessWebsite.trim(),
        businessLinks: businessLinks.filter(l => l.url.trim()).map(l => ({ type: l.type, url: l.url.trim() })),
        businessServices,
        businessABN: businessABN.trim(),
        businessInsuranceUrl: finalInsuranceUrl,
        businessInsuranceFileName: finalInsuranceFileName,
        businessVerifiedStatus: 'pending',
      };
      await saveProfile(uid, fields);
      onSubmitted?.(fields);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not submit application');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !submitting && businessName.trim() && businessEmail.trim() && businessPhone.trim()
    && businessABN.trim() && (insuranceFile || insuranceUrl);

  return (
    <Modal
      title="Apply to become a verified business"
      onClose={() => !submitting && onClose()}
      onConfirm={handleSubmit}
      confirmLabel={submitting ? 'Submitting…' : 'Submit for review'}
      confirmDisabled={!canSubmit}
    >
      <p className="pp__connect-note" style={{ marginTop: 0 }}>
        We manually review applications before granting verified-business status — this can take a few days. Once approved, you'll be able to create tours &amp; lessons and connect Stripe to get paid.
      </p>

      <label className="pp__field-label" htmlFor="bam-name">Business name</label>
      <input
        id="bam-name"
        className="pp__input"
        placeholder="e.g. Blue Ridge Motorcycle Tours"
        value={businessName}
        onChange={e => setBusinessName(e.target.value)}
      />

      <label className="pp__field-label" htmlFor="bam-email">Contact email</label>
      <input
        id="bam-email"
        className="pp__input"
        type="email"
        required
        value={businessEmail}
        onChange={e => setBusinessEmail(e.target.value)}
      />

      <label className="pp__field-label" htmlFor="bam-phone">Contact phone</label>
      <input
        id="bam-phone"
        className="pp__input"
        type="tel"
        required
        value={businessPhone}
        onChange={e => setBusinessPhone(e.target.value)}
      />

      <label className="pp__field-label" htmlFor="bam-website">Website (optional)</label>
      <input
        id="bam-website"
        className="pp__input"
        placeholder="https://…"
        value={businessWebsite}
        onChange={e => setBusinessWebsite(e.target.value)}
      />

      <label className="pp__field-label">Socials (optional)</label>
      <div className="pp__links">
        {businessLinks.map((l, i) => (
          <div className="pp__link-row" key={i}>
            <select value={l.type} onChange={e => updateLink(i, { type: e.target.value })} className="pp__link-type">
              {LINK_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <input
              className="pp__input pp__link-url"
              placeholder="https://…"
              value={l.url}
              onChange={e => updateLink(i, { url: e.target.value })}
            />
            <button type="button" className="pp__link-remove" onClick={() => removeLink(i)} aria-label="Remove">×</button>
          </div>
        ))}
        <button type="button" className="pp__btn pp__btn--outline pp__add-link" onClick={addLink}>+ Add link</button>
      </div>

      <label className="pp__field-label">Services offered</label>
      {SERVICE_OPTIONS.map(opt => (
        <label className="pp__check-row" key={opt.id}>
          <input type="checkbox" checked={businessServices.includes(opt.id)} onChange={() => toggleService(opt.id)} />
          <span>{opt.label}</span>
        </label>
      ))}

      <label className="pp__field-label" htmlFor="bam-abn" style={{ marginTop: 14 }}>ABN</label>
      <input
        id="bam-abn"
        className="pp__input"
        placeholder="11 digit Australian Business Number"
        value={businessABN}
        onChange={e => setBusinessABN(e.target.value)}
      />

      <label className="pp__field-label" htmlFor="bam-insurance">Public liability insurance</label>
      <input
        id="bam-insurance"
        className="pp__input"
        type="file"
        accept="image/*,application/pdf"
        onChange={e => setInsuranceFile(e.target.files?.[0] || null)}
      />
      <p className="pp__connect-note">
        Upload your certificate of currency (PDF or photo). {insuranceFile
          ? `Selected: ${insuranceFile.name}`
          : insuranceFileName
            ? `Currently on file: ${insuranceFileName}`
            : 'Required to apply.'}
      </p>

      {error && <p className="pp__connect-note" style={{ color: 'var(--color-danger, #dc2626)' }}>{error}</p>}
    </Modal>
  );
}
